/**
 * Admin Group Portal
 * Replica of Tour Details page with admin privileges
 * Includes group member management and one-on-one chat
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import {
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  CalendarDaysIcon,
  ClockIcon,
  MapPinIcon,
  TruckIcon,
  UserIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  PhoneIcon,
  EnvelopeIcon,
  EyeIcon,
  ArrowLeftIcon,
  ChatBubbleOvalLeftIcon,
  PlusIcon,
  BellIcon
} from '@heroicons/react/24/outline';

interface GroupMember {
  id: string;
  name: string;
  email: string;
  phone: string;
  nationality: string;
  age: number;
  profilePicture?: string;
  isAnonymous: boolean;
  joinedAt: string;
  isOnline: boolean;
  lastSeen: string;
  emergencyContact: {
    name: string;
    phone: string;
    relationship: string;
  };
  dietaryRequirements?: string;
  medicalConditions?: string;
  specialRequests?: string;
}

interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  message: string;
  timestamp: string;
  isAdmin: boolean;
  isGuide: boolean;
  type: 'text' | 'image' | 'system' | 'update';
  isRead: boolean;
}

interface GroupInfo {
  id: string;
  name: string;
  tourName: string;
  description: string;
  duration: string;
  startDate: string;
  endDate: string;
  status: 'preparing' | 'active' | 'completed';
  members: GroupMember[];
  guide?: {
    name: string;
    photo: string;
    experience: number;
    specialties: string[];
    phone: string;
    email: string;
    introduction?: string;
    isIntroduced: boolean;
  };
  vehicle?: {
    registration: string;
    type: string;
    capacity: number;
    features: string[];
    images: string[];
    driver: string;
    driverPhone: string;
    specifications: string[];
  };
  accommodation?: {
    name: string;
    type: string;
    location: string;
    images: string[];
    features: string[];
    contact: string;
    phone: string;
    amenities: string[];
  };
  itinerary: Array<{
    day: number;
    title: string;
    description: string;
    activities: string[];
    meals: string[];
    accommodation: string;
    isCompleted: boolean;
    startTime?: string;
    endTime?: string;
  }>;
  countdown: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  };
  updates: Array<{
    id: string;
    title: string;
    message: string;
    timestamp: string;
    type: 'info' | 'warning' | 'success' | 'swap';
    isRead: boolean;
  }>;
}

const AdminGroupPortal: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const { actualTheme } = useTheme();
  const navigate = useNavigate();
  const [groupInfo, setGroupInfo] = useState<GroupInfo | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedMember, setSelectedMember] = useState<GroupMember | null>(null);
  const [showMemberDetails, setShowMemberDetails] = useState(false);
  const [showOneOnOneChat, setShowOneOnOneChat] = useState(false);
  const [oneOnOneMessages, setOneOnOneMessages] = useState<ChatMessage[]>([]);
  const [oneOnOneMessage, setOneOnOneMessage] = useState('');

  useEffect(() => {
    // Check admin authentication - accept both main admin auth and portal auth
    const mainAdminAuth = localStorage.getItem('adminAuth');
    const portalAdminAuth = localStorage.getItem('adminPortalAuth');
    
    if (!mainAdminAuth && !portalAdminAuth) {
      navigate('/admin/portal-login');
      return;
    }

    loadGroupData();
  }, [groupId, navigate]);

  const loadGroupData = async () => {
    try {
      // Mock data - in real implementation, fetch from API based on groupId
      const groupData: Record<string, {
        name: string;
        tourName: string;
        description: string;
        duration: string;
        startDate: string;
        endDate: string;
        status: 'preparing' | 'active' | 'completed';
      }> = {
        'group_001': {
          name: 'Maasai Mara Group A',
          tourName: 'Maasai Mara Safari',
          description: 'Experience the Great Migration and witness the Big Five in their natural habitat. This 4-day safari takes you through the most spectacular wildlife viewing areas of the Maasai Mara.',
          duration: '4 days / 3 nights',
          startDate: '2024-02-15',
          endDate: '2024-02-18',
          status: 'preparing' as const
        },
        'group_002': {
          name: 'Amboseli Group B',
          tourName: 'Amboseli Safari',
          description: 'Discover the majestic elephants of Amboseli National Park with stunning views of Mount Kilimanjaro. This 3-day safari offers incredible wildlife photography opportunities.',
          duration: '3 days / 2 nights',
          startDate: '2024-02-20',
          endDate: '2024-02-22',
          status: 'active' as const
        },
        'group_003': {
          name: 'Tsavo Group C',
          tourName: 'Tsavo East Safari',
          description: 'Explore the vast wilderness of Tsavo East National Park, home to the famous red elephants and diverse wildlife. A 5-day adventure through Kenya\'s largest national park.',
          duration: '5 days / 4 nights',
          startDate: '2024-02-25',
          endDate: '2024-03-01',
          status: 'preparing' as const
        }
      };

      const currentGroup = groupData[groupId as keyof typeof groupData] || groupData['group_001'];
      
      const mockGroupInfo: GroupInfo = {
        id: groupId || 'group_001',
        name: currentGroup.name,
        tourName: currentGroup.tourName,
        description: currentGroup.description,
        duration: currentGroup.duration,
        startDate: currentGroup.startDate,
        endDate: currentGroup.endDate,
        status: currentGroup.status,
        members: [
          {
            id: 'member_001',
            name: 'John Doe',
            email: 'john@example.com',
            phone: '+254 700 000 001',
            nationality: 'Kenya',
            age: 35,
            isAnonymous: false,
            joinedAt: '2024-01-15T10:00:00Z',
            isOnline: true,
            lastSeen: '2024-01-25T10:30:00Z',
            emergencyContact: {
              name: 'Jane Doe',
              phone: '+254 700 000 002',
              relationship: 'Spouse'
            },
            dietaryRequirements: 'Vegetarian',
            medicalConditions: 'None',
            specialRequests: 'Early morning departure preferred'
          },
          {
            id: 'member_002',
            name: 'Sarah Smith',
            email: 'sarah@example.com',
            phone: '+254 700 000 003',
            nationality: 'USA',
            age: 28,
            isAnonymous: true,
            joinedAt: '2024-01-16T14:30:00Z',
            isOnline: false,
            lastSeen: '2024-01-24T15:45:00Z',
            emergencyContact: {
              name: 'Mike Smith',
              phone: '+254 700 000 004',
              relationship: 'Brother'
            },
            dietaryRequirements: 'None',
            medicalConditions: 'Allergic to nuts',
            specialRequests: 'Window seat preferred'
          }
        ],
        guide: {
          name: 'Peter Kimani',
          photo: '/images/guide-peter.jpg',
          experience: 8,
          specialties: ['Wildlife Photography', 'Bird Watching', 'Cultural Tours'],
          phone: '+254 700 000 100',
          email: 'peter@sighttrails.com',
          introduction: 'Meet your experienced guide Peter Kimani, who has been leading safaris in Maasai Mara for over 8 years. He specializes in wildlife photography and will help you capture the perfect shots.',
          isIntroduced: false
        },
        vehicle: {
          registration: 'KDA 123A',
          type: 'Toyota Land Cruiser',
          capacity: 7,
          features: ['4WD', 'Pop-up Roof', 'Refrigerator', 'First Aid Kit', 'GPS Navigation'],
          images: ['/images/vehicle-1.jpg', '/images/vehicle-2.jpg'],
          driver: 'John Mwangi',
          driverPhone: '+254 700 000 200',
          specifications: [
            'Engine: 4.5L V8 Diesel',
            'Transmission: Automatic',
            'Seating: 7 passengers',
            'Air Conditioning: Yes',
            'Safety: ABS, Airbags, Roll Cage'
          ]
        },
        accommodation: {
          name: 'Mara Serena Lodge',
          type: 'Luxury Lodge',
          location: 'Maasai Mara National Reserve',
          images: ['/images/lodge-1.jpg', '/images/lodge-2.jpg'],
          features: ['Restaurant', 'Bar', 'Swimming Pool', 'WiFi', 'Spa'],
          contact: 'Reservations Manager',
          phone: '+254 700 000 300',
          amenities: [
            '24/7 Room Service',
            'Laundry Service',
            'Currency Exchange',
            'Gift Shop',
            'Conference Facilities'
          ]
        },
        itinerary: [
          {
            day: 1,
            title: 'Arrival & Welcome Game Drive',
            description: 'Arrive in Maasai Mara and enjoy your first game drive in search of the Big Five',
            activities: [
              'Airport pickup from Wilson Airport',
              'Scenic drive to Maasai Mara (4 hours)',
              'Lunch at lodge',
              'Afternoon game drive',
              'Sunset photography session'
            ],
            meals: ['Lunch', 'Dinner'],
            accommodation: 'Mara Serena Lodge',
            isCompleted: false,
            startTime: '08:00',
            endTime: '18:00'
          },
          {
            day: 2,
            title: 'Full Day Safari Adventure',
            description: 'Explore the vast plains of Maasai Mara with early morning and afternoon game drives',
            activities: [
              'Early morning game drive (6:00 AM)',
              'Breakfast in the bush',
              'Mid-morning game drive',
              'Lunch at lodge',
              'Afternoon game drive',
              'Cultural visit to Maasai village'
            ],
            meals: ['Breakfast', 'Lunch', 'Dinner'],
            accommodation: 'Mara Serena Lodge',
            isCompleted: false,
            startTime: '06:00',
            endTime: '19:00'
          }
        ],
        countdown: (() => {
          const startDate = new Date(currentGroup.startDate);
          const now = new Date();
          const diffTime = startDate.getTime() - now.getTime();
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          const diffHours = Math.ceil((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const diffMinutes = Math.ceil((diffTime % (1000 * 60 * 60)) / (1000 * 60));
          
          return {
            days: Math.max(0, diffDays),
            hours: Math.max(0, diffHours),
            minutes: Math.max(0, diffMinutes),
            seconds: 0
          };
        })(),
        updates: [
          {
            id: 'update_001',
            title: 'Group Confirmed',
            message: 'Your group has been confirmed with 6 travelers. Welcome to Maasai Mara Group A!',
            timestamp: '2024-01-20T09:00:00Z',
            type: 'success',
            isRead: true
          }
        ]
      };

      const mockChatMessages: ChatMessage[] = [
        {
          id: 'msg_001',
          senderId: 'admin',
          senderName: 'SightTrails Admin',
          message: 'Welcome to your Maasai Mara Group A! We\'re excited to have you join us for this amazing safari adventure.',
          timestamp: '2024-01-20T09:00:00Z',
          isAdmin: true,
          isGuide: false,
          type: 'text',
          isRead: true
        },
        {
          id: 'msg_002',
          senderId: 'member_001',
          senderName: 'John Doe',
          message: 'Thank you! I\'m really looking forward to this safari. What should I pack?',
          timestamp: '2024-01-20T09:15:00Z',
          isAdmin: false,
          isGuide: false,
          type: 'text',
          isRead: true
        }
      ];

      setGroupInfo(mockGroupInfo);
      setChatMessages(mockChatMessages);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load group data:', error);
      setLoading(false);
    }
  };

  const handleSendMessage = () => {
    if (!newMessage.trim()) return;

    const newMsg: ChatMessage = {
      id: `msg_${Date.now()}`,
      senderId: 'admin',
      senderName: 'SightTrails Admin',
      message: newMessage,
      timestamp: new Date().toISOString(),
      isAdmin: true,
      isGuide: false,
      type: 'text',
      isRead: true
    };

    setChatMessages(prev => [...prev, newMsg]);
    setNewMessage('');
  };

  const handleOneOnOneMessage = () => {
    if (!oneOnOneMessage.trim() || !selectedMember) return;

    const newMsg: ChatMessage = {
      id: `msg_${Date.now()}`,
      senderId: 'admin',
      senderName: 'SightTrails Admin',
      message: oneOnOneMessage,
      timestamp: new Date().toISOString(),
      isAdmin: true,
      isGuide: false,
      type: 'text',
      isRead: true
    };

    setOneOnOneMessages(prev => [...prev, newMsg]);
    setOneOnOneMessage('');
  };

  const openOneOnOneChat = (member: GroupMember) => {
    setSelectedMember(member);
    setShowOneOnOneChat(true);
    setShowMemberDetails(false);
    
    // Load one-on-one messages for this member
    const mockOneOnOneMessages: ChatMessage[] = [
      {
        id: 'oneonone_001',
        senderId: 'admin',
        senderName: 'SightTrails Admin',
        message: `Hello ${member.name}, how can I help you with your safari preparation?`,
        timestamp: '2024-01-20T09:00:00Z',
        isAdmin: true,
        isGuide: false,
        type: 'text',
        isRead: true
      }
    ];
    setOneOnOneMessages(mockOneOnOneMessages);
  };

  const getUpdateIcon = (type: string) => {
    switch (type) {
      case 'success': return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'warning': return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
      case 'info': return <ClockIcon className="w-5 h-5 text-blue-500" />;
      case 'swap': return <ArrowRightIcon className="w-5 h-5 text-purple-500" />;
      default: return <ClockIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getUpdateColor = (type: string) => {
    switch (type) {
      case 'success': return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
      case 'warning': return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
      case 'info': return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
      case 'swap': return 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800';
      default: return 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!groupInfo) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Group Not Found</h2>
        <p className="text-gray-600 dark:text-gray-300">The group you're looking for doesn't exist or you don't have access to it.</p>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-all duration-300 ${
      actualTheme === 'dark' ? 'bg-slate-900' : 'bg-neutral-50'
    }`}>
      {/* Header Section - No Background */}
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center mb-4">
                <button
                  onClick={() => navigate('/admin/groups')}
                  className={`flex items-center mr-6 transition-colors duration-300 ${
                    actualTheme === 'dark' ? 'text-slate-400 hover:text-slate-200' : 'text-neutral-600 hover:text-neutral-800'
                  }`}
                >
                  <ArrowLeftIcon className="w-5 h-5 mr-2" />
                  Back to Groups
                </button>
                <div className={`px-3 py-1 rounded-full text-sm font-medium transition-colors duration-300 ${
                  actualTheme === 'dark' 
                    ? 'bg-blue-900/30 text-blue-300 border border-blue-700/50' 
                    : 'bg-blue-100 text-blue-700 border border-blue-200'
                }`}>
                  Admin View
                </div>
              </div>
              <h1 className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-4 transition-colors duration-300 ${
                actualTheme === 'dark' ? 'text-slate-200' : 'text-neutral-900'
              }`}>{groupInfo?.name || 'Loading...'}</h1>
              <div className={`flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 transition-colors duration-300 ${
                actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
              }`}>
                <div className="flex items-center">
                  <MapPinIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  <span className="text-sm sm:text-base">{groupInfo?.tourName || 'Loading...'}</span>
                </div>
                <div className="flex items-center">
                  <ClockIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  <span className="text-sm sm:text-base">{groupInfo?.duration || 'Loading...'}</span>
                </div>
                <div className="flex items-center">
                  <CalendarDaysIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  <span className="text-sm sm:text-base">
                    {groupInfo?.startDate && groupInfo?.endDate 
                      ? `${new Date(groupInfo.startDate).toLocaleDateString()} - ${new Date(groupInfo.endDate).toLocaleDateString()}`
                      : 'Loading...'
                    }
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center lg:justify-end space-x-4">
              <div className="text-center">
                <div className={`text-2xl sm:text-3xl font-bold transition-colors duration-300 ${
                  actualTheme === 'dark' ? 'text-slate-200' : 'text-neutral-900'
                }`}>
                  {groupInfo?.countdown ? `${groupInfo.countdown.days}d ${groupInfo.countdown.hours}h ${groupInfo.countdown.minutes}m` : 'Loading...'}
                </div>
                <div className={`text-sm transition-colors duration-300 ${
                  actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                }`}>
                  Until Safari
                </div>
              </div>
              <button className={`p-3 rounded-modern transition-all duration-300 hover-magnify-sm modern-focus ${
                actualTheme === 'dark' 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-green-500 hover:bg-green-600'
              } text-white`}>
                <BellIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">

        {/* Tab Navigation - Similar to Tour Details */}
        <div className={`border-b mb-6 sm:mb-8 transition-colors duration-300 ${
          actualTheme === 'dark' ? 'border-slate-600' : 'border-neutral-200'
        }`}>
          <nav className="flex flex-wrap gap-2 sm:gap-4 lg:space-x-8">
            {[
              { id: 'overview', name: 'Overview', icon: UserGroupIcon },
              { id: 'members', name: 'Group Members', icon: UserIcon },
              { id: 'itinerary', name: 'Itinerary', icon: CalendarDaysIcon },
              { id: 'logistics', name: 'Logistics', icon: TruckIcon },
              { id: 'chat', name: 'Group Chat', icon: ChatBubbleLeftRightIcon },
              { id: 'guide', name: 'Guide', icon: UserIcon }
            ].map((tab) => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 sm:py-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm transition-all duration-200 hover-text-magnify modern-focus ${
                    activeTab === tab.id
                      ? 'border-primary-600 text-primary-600'
                      : actualTheme === 'dark'
                        ? 'border-transparent text-slate-400 hover:text-slate-300 hover:border-slate-500'
                        : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
                  }`}
                >
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content - Similar to Tour Details Layout */}
        <div className="flex flex-col lg:grid lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 order-1 lg:order-1">
            <div className="space-y-8">
              {activeTab === 'overview' && (
                <div>
                  <h3 className={`text-2xl font-bold mb-4 transition-colors duration-300 ${
                    actualTheme === 'dark' ? 'text-slate-200' : 'text-primary-900'
                  }`}>About This Safari Group</h3>
                  <p className={`leading-relaxed mb-6 transition-colors duration-300 ${
                    actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                  }`}>{groupInfo.description}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className={`text-lg font-semibold mb-3 transition-colors duration-300 ${
                        actualTheme === 'dark' ? 'text-slate-200' : 'text-primary-900'
                      }`}>Group Highlights</h4>
                      <ul className="space-y-2">
                        <li className="flex items-center">
                          <CheckCircleIcon className={`h-5 w-5 mr-3 flex-shrink-0 transition-colors duration-300 ${
                            actualTheme === 'dark' ? 'text-primary-400' : 'text-primary-600'
                          }`} />
                          <span className={`transition-colors duration-300 ${
                            actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                          }`}>Expert safari guide</span>
                        </li>
                        <li className="flex items-center">
                          <CheckCircleIcon className={`h-5 w-5 mr-3 flex-shrink-0 transition-colors duration-300 ${
                            actualTheme === 'dark' ? 'text-primary-400' : 'text-primary-600'
                          }`} />
                          <span className={`transition-colors duration-300 ${
                            actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                          }`}>All logistics included</span>
                        </li>
                        <li className="flex items-center">
                          <CheckCircleIcon className={`h-5 w-5 mr-3 flex-shrink-0 transition-colors duration-300 ${
                            actualTheme === 'dark' ? 'text-primary-400' : 'text-primary-600'
                          }`} />
                          <span className={`transition-colors duration-300 ${
                            actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                          }`}>Group communication</span>
                        </li>
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className={`text-lg font-semibold mb-3 transition-colors duration-300 ${
                        actualTheme === 'dark' ? 'text-slate-200' : 'text-primary-900'
                      }`}>Recent Updates</h4>
                      <div className="space-y-3">
                        {groupInfo.updates.map((update) => (
                          <div key={update.id} className={`p-3 rounded-modern border transition-colors duration-300 ${
                            actualTheme === 'dark'
                              ? 'bg-slate-800/50 border-slate-600/30'
                              : 'bg-neutral-50 border-neutral-200'
                          }`}>
                            <div className="flex items-start">
                              <div className="flex-shrink-0">
                                {getUpdateIcon(update.type)}
                              </div>
                              <div className="ml-3 flex-1">
                                <h4 className={`font-medium transition-colors duration-300 ${
                                  actualTheme === 'dark' ? 'text-slate-200' : 'text-neutral-900'
                                }`}>{update.title}</h4>
                                <p className={`text-sm mt-1 transition-colors duration-300 ${
                                  actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                                }`}>{update.message}</p>
                                <p className={`text-xs mt-2 transition-colors duration-300 ${
                                  actualTheme === 'dark' ? 'text-slate-500' : 'text-neutral-500'
                                }`}>
                                  {new Date(update.timestamp).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'members' && (
                <div>
                  <h3 className={`text-2xl font-bold mb-4 transition-colors duration-300 ${
                    actualTheme === 'dark' ? 'text-slate-200' : 'text-primary-900'
                  }`}>Group Members ({groupInfo.members.length})</h3>
                  
                  <div className="flex items-center justify-between mb-6">
                    <p className={`transition-colors duration-300 ${
                      actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                    }`}>
                      Manage and communicate with group members
                    </p>
                    <button className="flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-modern transition-all duration-300 hover-magnify-sm modern-focus">
                      <PlusIcon className="w-5 h-5 mr-2" />
                      Add Member
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {groupInfo.members.map((member) => (
                      <div key={member.id} className={`p-4 rounded-modern border transition-all duration-300 hover-magnify-sm ${
                        actualTheme === 'dark'
                          ? 'bg-slate-800/50 border-slate-600/30 hover:bg-slate-700/50'
                          : 'bg-neutral-50 border-neutral-200 hover:bg-neutral-100'
                      }`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold mr-3 transition-colors duration-300 ${
                              actualTheme === 'dark' ? 'bg-primary-600' : 'bg-primary-500'
                            }`}>
                              {member.isAnonymous ? '?' : member.name.charAt(0)}
                            </div>
                            <div>
                              <div className={`font-medium transition-colors duration-300 ${
                                actualTheme === 'dark' ? 'text-slate-200' : 'text-neutral-900'
                              }`}>
                                {member.isAnonymous ? 'Anonymous Traveler' : member.name}
                              </div>
                              <div className={`text-sm transition-colors duration-300 ${
                                actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-500'
                              }`}>
                                {member.isOnline ? 'Online' : `Last seen: ${new Date(member.lastSeen).toLocaleString()}`}
                              </div>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => openOneOnOneChat(member)}
                              className="p-2 text-green-500 hover:text-green-600 transition-colors hover-magnify-sm"
                              title="Start Chat"
                            >
                              <ChatBubbleOvalLeftIcon className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedMember(member);
                                setShowMemberDetails(true);
                                setShowOneOnOneChat(false);
                              }}
                              className="p-2 text-blue-500 hover:text-blue-600 transition-colors hover-magnify-sm"
                              title="View Details"
                            >
                              <EyeIcon className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                        
                        <div className={`text-sm transition-colors duration-300 ${
                          actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                        }`}>
                          <div className="flex items-center mb-1">
                            <EnvelopeIcon className={`w-4 h-4 mr-2 transition-colors duration-300 ${
                              actualTheme === 'dark' ? 'text-slate-500' : 'text-neutral-500'
                            }`} />
                            {member.email}
                          </div>
                          <div className="flex items-center mb-1">
                            <PhoneIcon className={`w-4 h-4 mr-2 transition-colors duration-300 ${
                              actualTheme === 'dark' ? 'text-slate-500' : 'text-neutral-500'
                            }`} />
                            {member.phone}
                          </div>
                          <div className="flex items-center">
                            <MapPinIcon className={`w-4 h-4 mr-2 transition-colors duration-300 ${
                              actualTheme === 'dark' ? 'text-slate-500' : 'text-neutral-500'
                            }`} />
                            {member.nationality}, Age: {member.age}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'chat' && (
                <div>
                  <h3 className={`text-2xl font-bold mb-4 transition-colors duration-300 ${
                    actualTheme === 'dark' ? 'text-slate-200' : 'text-primary-900'
                  }`}>Group Chat</h3>
                  
                  <div className={`rounded-2xl shadow-modern backdrop-blur-sm border flex flex-col h-96 transition-all duration-300 ${
                    actualTheme === 'dark'
                      ? 'bg-gradient-to-br from-slate-800/90 to-slate-700/90 border-slate-600/30'
                      : 'bg-white/90 border-neutral-200/50'
                  }`}>
                    <div className={`p-4 border-b transition-colors duration-300 ${
                      actualTheme === 'dark' ? 'border-slate-600/30' : 'border-neutral-200'
                    }`}>
                      <h4 className={`text-lg font-semibold transition-colors duration-300 ${
                        actualTheme === 'dark' ? 'text-slate-200' : 'text-neutral-900'
                      }`}>Group Chat</h4>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                      {chatMessages.map((message) => (
                        <div key={message.id} className={`flex ${message.isAdmin ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-modern transition-all duration-300 ${
                            message.isAdmin
                              ? 'bg-green-500 text-white'
                              : actualTheme === 'dark'
                                ? 'bg-slate-700 text-slate-200'
                                : 'bg-neutral-100 text-neutral-900'
                          }`}>
                            <div className="text-xs font-medium mb-1">
                              {message.senderName} {message.isAdmin && '(Admin)'}
                            </div>
                            <div className="text-sm">{message.message}</div>
                            <div className="text-xs opacity-75 mt-1">
                              {new Date(message.timestamp).toLocaleTimeString()}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className={`p-4 border-t transition-colors duration-300 ${
                      actualTheme === 'dark' ? 'border-slate-600/30' : 'border-neutral-200'
                    }`}>
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Type your message..."
                          className={`flex-1 p-3 border rounded-modern focus:ring-2 focus:outline-none transition-all duration-300 ${
                            actualTheme === 'dark'
                              ? 'bg-slate-700 border-slate-600 text-white focus:ring-primary-500'
                              : 'bg-white border-gray-300 text-gray-900 focus:ring-primary-500'
                          }`}
                          onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                        />
                        <button
                          onClick={handleSendMessage}
                          className="px-4 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-modern transition-all duration-300 hover-magnify-sm modern-focus"
                        >
                          Send
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Other tabs content similar to client portal */}
            </div>
          </div>

          {/* Right Column - Sidebar - Similar to Tour Details */}
          <div className="order-2 lg:order-2">
            <div className="space-y-6">
              {/* Safari Countdown */}
              <div className={`rounded-2xl shadow-modern backdrop-blur-sm border p-6 transition-all duration-300 ${
                actualTheme === 'dark'
                  ? 'bg-gradient-to-br from-slate-800/90 to-slate-700/90 border-slate-600/30'
                  : 'bg-white/90 border-neutral-200/50'
              }`}>
                <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
                  actualTheme === 'dark' ? 'text-slate-200' : 'text-primary-900'
                }`}>Safari Countdown</h3>
                <div className="text-center">
                  <div className={`text-3xl font-bold transition-colors duration-300 ${
                    actualTheme === 'dark' ? 'text-primary-400' : 'text-primary-600'
                  }`}>
                    {groupInfo.countdown.days}
                  </div>
                  <div className={`text-sm transition-colors duration-300 ${
                    actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                  }`}>Days</div>
                  <div className={`text-2xl font-bold mt-2 transition-colors duration-300 ${
                    actualTheme === 'dark' ? 'text-primary-400' : 'text-primary-600'
                  }`}>
                    {groupInfo.countdown.hours}
                  </div>
                  <div className={`text-sm transition-colors duration-300 ${
                    actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                  }`}>Hours</div>
                  <div className={`text-xl font-bold mt-2 transition-colors duration-300 ${
                    actualTheme === 'dark' ? 'text-primary-400' : 'text-primary-600'
                  }`}>
                    {groupInfo.countdown.minutes}
                  </div>
                  <div className={`text-sm transition-colors duration-300 ${
                    actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                  }`}>Minutes</div>
                </div>
              </div>

              {/* Admin Actions */}
              <div className={`rounded-2xl shadow-modern backdrop-blur-sm border p-6 transition-all duration-300 ${
                actualTheme === 'dark'
                  ? 'bg-gradient-to-br from-slate-800/90 to-slate-700/90 border-slate-600/30'
                  : 'bg-white/90 border-neutral-200/50'
              }`}>
                <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
                  actualTheme === 'dark' ? 'text-slate-200' : 'text-primary-900'
                }`}>Admin Actions</h3>
                <div className="space-y-3">
                  <button className={`w-full flex items-center p-3 rounded-modern transition-all duration-300 hover-magnify-sm modern-focus ${
                    actualTheme === 'dark'
                      ? 'bg-blue-900/20 hover:bg-blue-900/30 border border-blue-800/30'
                      : 'bg-blue-50 hover:bg-blue-100 border border-blue-200'
                  }`}>
                    <UserIcon className="w-5 h-5 mr-3 text-blue-500" />
                    <span className={`text-sm font-medium transition-colors duration-300 ${
                      actualTheme === 'dark' ? 'text-blue-300' : 'text-blue-700'
                    }`}>Manage Members</span>
                  </button>
                  <button className={`w-full flex items-center p-3 rounded-modern transition-all duration-300 hover-magnify-sm modern-focus ${
                    actualTheme === 'dark'
                      ? 'bg-green-900/20 hover:bg-green-900/30 border border-green-800/30'
                      : 'bg-green-50 hover:bg-green-100 border border-green-200'
                  }`}>
                    <ChatBubbleLeftRightIcon className="w-5 h-5 mr-3 text-green-500" />
                    <span className={`text-sm font-medium transition-colors duration-300 ${
                      actualTheme === 'dark' ? 'text-green-300' : 'text-green-700'
                    }`}>Send Group Message</span>
                  </button>
                  <button className={`w-full flex items-center p-3 rounded-modern transition-all duration-300 hover-magnify-sm modern-focus ${
                    actualTheme === 'dark'
                      ? 'bg-purple-900/20 hover:bg-purple-900/30 border border-purple-800/30'
                      : 'bg-purple-50 hover:bg-purple-100 border border-purple-200'
                  }`}>
                    <TruckIcon className="w-5 h-5 mr-3 text-purple-500" />
                    <span className={`text-sm font-medium transition-colors duration-300 ${
                      actualTheme === 'dark' ? 'text-purple-300' : 'text-purple-700'
                    }`}>Update Logistics</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Member Details Modal */}
        {showMemberDetails && selectedMember && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-2xl w-full p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold">Member Details</h3>
                <button
                  onClick={() => setShowMemberDetails(false)}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <ArrowRightIcon className="w-6 h-6 rotate-180" />
                </button>
              </div>

              <div className="space-y-6">
                <div className="flex items-center">
                  <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center text-white text-xl font-semibold mr-4">
                    {selectedMember.isAnonymous ? '?' : selectedMember.name.charAt(0)}
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold">
                      {selectedMember.isAnonymous ? 'Anonymous Traveler' : selectedMember.name}
                    </h4>
                    <p className="text-gray-600 dark:text-gray-300">
                      {selectedMember.nationality}, Age: {selectedMember.age}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h5 className="font-semibold mb-2">Contact Information</h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center">
                        <EnvelopeIcon className="w-4 h-4 mr-2 text-gray-500" />
                        {selectedMember.email}
                      </div>
                      <div className="flex items-center">
                        <PhoneIcon className="w-4 h-4 mr-2 text-gray-500" />
                        {selectedMember.phone}
                      </div>
                    </div>
                  </div>
                  <div>
                    <h5 className="font-semibold mb-2">Emergency Contact</h5>
                    <div className="space-y-2 text-sm">
                      <div>{selectedMember.emergencyContact.name}</div>
                      <div>{selectedMember.emergencyContact.phone}</div>
                      <div>{selectedMember.emergencyContact.relationship}</div>
                    </div>
                  </div>
                </div>

                {selectedMember.dietaryRequirements && (
                  <div>
                    <h5 className="font-semibold mb-2">Dietary Requirements</h5>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{selectedMember.dietaryRequirements}</p>
                  </div>
                )}

                {selectedMember.medicalConditions && (
                  <div>
                    <h5 className="font-semibold mb-2">Medical Conditions</h5>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{selectedMember.medicalConditions}</p>
                  </div>
                )}

                {selectedMember.specialRequests && (
                  <div>
                    <h5 className="font-semibold mb-2">Special Requests</h5>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{selectedMember.specialRequests}</p>
                  </div>
                )}

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowMemberDetails(false);
                      openOneOnOneChat(selectedMember);
                    }}
                    className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-xl transition-colors"
                  >
                    Start Chat
                  </button>
                  <button
                    onClick={() => setShowMemberDetails(false)}
                    className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-xl transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* One-on-One Chat Modal */}
        {showOneOnOneChat && selectedMember && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-4xl w-full h-96 flex flex-col">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold mr-3">
                      {selectedMember.isAnonymous ? '?' : selectedMember.name.charAt(0)}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">
                        Chat with {selectedMember.isAnonymous ? 'Anonymous Traveler' : selectedMember.name}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {selectedMember.isOnline ? 'Online' : 'Offline'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowOneOnOneChat(false)}
                    className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    <ArrowRightIcon className="w-6 h-6 rotate-180" />
                  </button>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {oneOnOneMessages.map((message) => (
                  <div key={message.id} className={`flex ${message.isAdmin ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-xl ${
                      message.isAdmin
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                    }`}>
                      <div className="text-xs font-medium mb-1">
                        {message.senderName} {message.isAdmin && '(Admin)'}
                      </div>
                      <div className="text-sm">{message.message}</div>
                      <div className="text-xs opacity-75 mt-1">
                        {new Date(message.timestamp).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={oneOnOneMessage}
                    onChange={(e) => setOneOnOneMessage(e.target.value)}
                    placeholder="Type your message..."
                    className="flex-1 p-3 border rounded-xl focus:ring-2 focus:outline-none"
                    onKeyPress={(e) => e.key === 'Enter' && handleOneOnOneMessage()}
                  />
                  <button
                    onClick={handleOneOnOneMessage}
                    className="px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl transition-colors"
                  >
                    Send
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminGroupPortal;





